import "./search.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { Backdrop, debounce, type DebouncedFunction } from "@ts/utils";

type SearchTrigger = HTMLButtonElement | HTMLAnchorElement;

type SearchResultsPayload = {
    query: string;
    results: unknown;
    trigger: SearchTrigger;
};

type SearchErrorPayload = {
    query: string;
    error: unknown;
    trigger: SearchTrigger;
};

type SearchHook = (trigger: SearchTrigger) => void | Promise<void>;
type SearchResultsHook = (payload: SearchResultsPayload) => void | Promise<void>;
type SearchErrorHook = (payload: SearchErrorPayload) => void | Promise<void>;
type SearchOptions = {
    onOpen?: SearchHook;
    onClose?: SearchHook;
    onResults?: SearchResultsHook;
    onError?: SearchErrorHook;
    searchContainerSelector?: string;
    autocompleteUrl?: string;
    autocompleteDelay?: number;
    autocompleteInputSelector?: string;
};

export class Search {
    private readonly id: string;
    private static registry = new ComponentRegistry<Search>('search');
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    private mobileBreakpoint = window.matchMedia('(max-width: 991px)');
    private searchContainer: HTMLElement | null = null;
    private backdrop: Backdrop | null = null;
    private isOpen = false;
    private readonly abortController = new AbortController();
    private readonly autocompleteDelay: number;    
    private processAutocompleteRequest: DebouncedFunction<[string]>;
    private activeAutocompleteController: AbortController | null = null;
    private readonly inputSelector: string;
    private clearButton: HTMLButtonElement | null = null;
    private inputElement: HTMLInputElement | null = null;

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: SearchOptions
    ) {
        this.id = Search.registry.register(trigger, this);
        this.trigger = trigger;
        this.searchContainer = trigger.closest('.nav-search')
            ?.querySelector(options.searchContainerSelector ?? '.search-container') ?? null;

        this.autocompleteDelay = options.autocompleteDelay ?? 300;
        this.inputSelector = options.autocompleteInputSelector ?? 'input[type="text"]';
        this.processAutocompleteRequest = debounce(
            (query: string) => { void this.fetchAutocompleteAsync(query); },
            this.autocompleteDelay
        );

        this.setClickEvent();
        this.bindEscapeKey();
        this.bindInputEvent();
        this.setupClearButton();
    }

    private handleEnterKey(e: KeyboardEvent): void {
        if (e.key === 'Enter' && this.inputElement) {
            const query = this.inputElement.value.trim();
            if (query.length === 0) return;

            const endpoint = document.getElementById('searchNavTrigger')?.getAttribute('href');
            window.location.href = endpoint ? `${endpoint}?Query=${encodeURIComponent(query)}` : '#';
        }
    }

    private bindInputEvent(): void {
        const input = this.searchContainer?.querySelector<HTMLInputElement>(this.inputSelector);
        if (!input) return;

        this.inputElement = input;
        input.addEventListener("keydown", (e) => {
            this.handleEnterKey(e);
        });
        input.addEventListener("input", (event: Event) => {
            const target = event.target as HTMLInputElement;
            const query = target.value.trim();

            this.toggleClearButton(query.length > 0);

            if (!this.options.autocompleteUrl) {
                return;
            }

            if (query.length === 0) {
                this.processAutocompleteRequest.cancel();
                this.cancelActiveAutocompleteRequest();
                this.setResultsState(false);
                
                if (typeof this.options.onResults === "function") {
                    void this.options.onResults({
                        query: "",
                        results: [],
                        trigger: this.trigger
                    });
                }
                return;
            }

            this.processAutocompleteRequest(query);
        }, { signal: this.abortController.signal });
    }

    private setupClearButton(): void {
        const dialog = this.searchContainer?.querySelector<HTMLElement>(".search-dialog");
        if (!dialog || this.clearButton) return;

        const button = document.createElement("button");
        button.type = "button";
        button.className = "search-clear-btn";
        button.setAttribute("aria-label", "Clear search");
        button.setAttribute("title", "Clear search");
        button.innerHTML = "<i class='fa-solid fa-circle-xmark'></i>";
        button.style.display = "none";

        button.addEventListener("click", () => {
            this.clearSearch();
        }, { signal: this.abortController.signal });

        dialog.appendChild(button);
        this.clearButton = button;
    }

    private toggleClearButton(show: boolean): void {
        if (!this.clearButton) return;
        this.clearButton.style.display = show ? "inline-flex" : "none";
    }

    private cancelActiveAutocompleteRequest(): void {
        this.activeAutocompleteController?.abort();
        this.activeAutocompleteController = null;
    }

    private clearSearch(): void {
        if (!this.inputElement) return;
        this.processAutocompleteRequest.cancel();
        this.cancelActiveAutocompleteRequest();

        this.inputElement.value = "";
        this.toggleClearButton(false);
        this.setResultsState(false);

        if (typeof this.options.onResults === "function") {
            void this.options.onResults({
                query: "",
                results: [],
                trigger: this.trigger
            });
        }

        this.inputElement.focus();
    }

    private async fetchAutocompleteAsync(query: string): Promise<void> {
        if (!this.options.autocompleteUrl) return;

        this.cancelActiveAutocompleteRequest();
        const requestController = new AbortController();
        this.activeAutocompleteController = requestController;

        const separator = this.options.autocompleteUrl.includes('?') ? '&' : '?';
        const requestUrl = this.options.autocompleteUrl + separator + 'Query=' + encodeURIComponent(query);

        try {
            const response = await fetch(requestUrl, {
                signal: requestController.signal
            });

            if (!response.ok) {
                throw new Error('Autocomplete request failed with status ' + response.status);
            }

            const data = await response.json();
            this.setResultsState(query.length > 0);

            if (typeof this.options.onResults === 'function') {
                await this.options.onResults({
                    query,
                    results: data.results || [],
                    trigger: this.trigger
                });
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            if (typeof this.options.onError === 'function') {
                await this.options.onError({
                    query,
                    error,
                    trigger: this.trigger
                });
            }
        } finally {
            if (this.activeAutocompleteController === requestController) {
                this.activeAutocompleteController = null;
            }
        }
    }

    private setBodyClass(isOpen: boolean): void {
        document.body.classList.toggle('search-open', isOpen);
    }

    private setResultsState(hasResults: boolean): void {
        if (!this.searchContainer) return;
        const shouldShowResults = this.isOpen && hasResults;
        this.searchContainer.classList.toggle("search-container--results", shouldShowResults);
    }

    private setClickEvent(): void {
        this.trigger.addEventListener('click', (e) => {
            if (this.mobileBreakpoint.matches) return;
            e.preventDefault();
            this.toggle();
        }, { signal: this.abortController.signal });
    }

    private bindEscapeKey(): void {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        }, { signal: this.abortController.signal });
    }

    public toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    public open(): void {
        if (this.isOpen || !this.searchContainer) return;

        this.isOpen = true;
        this.searchContainer.classList.add('search-container--open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.setBodyClass(true);

        this.backdrop = new Backdrop(() => this.close());
        this.backdrop.show();

        const input = this.searchContainer.querySelector<HTMLInputElement>(this.inputSelector);
        input?.focus();

        if (typeof this.options.onOpen === 'function') {
            this.options.onOpen(this.trigger);
        }
    }

    public close(): void {
        if (!this.isOpen || !this.searchContainer) return;

        this.isOpen = false;
        this.searchContainer.classList.remove('search-container--open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.backdrop?.hide();
        this.backdrop = null;
        this.setBodyClass(false);
        this.clearSearch();
        if (typeof this.options.onClose === 'function') {
            this.options.onClose(this.trigger);
        }
    }

    public destroy(): void {
        this.close();
        this.abortController.abort();
        this.processAutocompleteRequest.cancel();
        this.cancelActiveAutocompleteRequest();
        Search.registry.unregister(this.id);
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: SearchOptions = {}
    ): Search[] | null {
        const triggers = ComponentRegistry.processTargets<Search, HTMLElement>(
            Search.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers?.length) return null;

        return triggers.map(trigger => new Search(trigger, options));
    }

    public static getSearch(id: string): Search | null {
        return Search.registry.get(id) ?? null;
    }
}